import React, { useEffect, useRef, useState } from 'react';
import { Heart, DollarSign, Users, Star, Search, Grid, List, MapPin, Filter, Eye } from 'lucide-react';
import { sampleVenues, venueLocations, venueTypes } from '../data/sampleVenues';
import type { Venue, VenueFilters } from '../types/venues';
import { getPublicVenues } from '../lib/admin';
import { transformAdminVenueToVenue } from '../utils/dataTransform';

export default function VenueFinder() {
  const sparklesRef = useRef<HTMLDivElement>(null);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [savedVenues, setSavedVenues] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<VenueFilters>({
    searchTerm: '',
    maxPrice: 1000000,
    minCapacity: 50,
    venueType: 'all',
    location: 'All Locations'
  });

  useEffect(() => {
    loadVenues();
  }, []);

  async function loadVenues() {
    try {
      setLoading(true);
      const dbVenues = await getPublicVenues();
      const transformedVenues = dbVenues.map(transformAdminVenueToVenue);
      setVenues([...transformedVenues, ...sampleVenues]);
    } catch (error) {
      console.error('Error loading venues:', error);
      setVenues(sampleVenues);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const createSparkle = () => {
      if (!sparklesRef.current) return;
      
      const sparkle = document.createElement('div');
      sparkle.className = 'sparkle';
      
      const startX = Math.random() * window.innerWidth;
      const endX = startX + (Math.random() * 100 - 50);
      
      sparkle.style.setProperty('--start-x', `${startX}px`);
      sparkle.style.setProperty('--end-x', `${endX}px`);
      sparkle.style.left = `${startX}px`;
      
      sparklesRef.current.appendChild(sparkle);
      
      setTimeout(() => sparkle.remove(), 3000);
    };

    const interval = setInterval(createSparkle, 200);
    return () => clearInterval(interval);
  }, []);

  const toggleSaveVenue = (venueId: string) => {
    setSavedVenues(prev => 
      prev.includes(venueId) 
        ? prev.filter(id => id !== venueId)
        : [...prev, venueId]
    );
  };

  const filteredVenues = venues.filter(venue => {
    const matchesSearch = venue.name.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
                         venue.location.toLowerCase().includes(filters.searchTerm.toLowerCase());
    const matchesPrice = venue.price <= filters.maxPrice;
    const matchesCapacity = venue.capacity >= filters.minCapacity;
    const matchesType = filters.venueType === 'all' || venue.venueType === filters.venueType;
    const matchesLocation = filters.location === 'All Locations' || venue.city === filters.location;
    
    return matchesSearch && matchesPrice && matchesCapacity && matchesType && matchesLocation;
  });

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Sparkles Container */}
      <div ref={sparklesRef} className="sparkles-container" />

      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold text-white">This Option is Coming Soon</h1>
        <h1 className="text-4xl font-bold text-white">Find Your Perfect Venue</h1>
        <p className="text-xl text-gray-300 max-w-2xl mx-auto">
          Discover beautiful venues that match your vision and budget
        </p>
      </div>

      {/* Search and Filters */}
      <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="md:col-span-2 relative">
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-white/40" />
            <input
              type="text"
              placeholder="Search venues by name or location..."
              value={filters.searchTerm}
              onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
              className="pl-10 luxury-input"
            />
          </div>
          
          <div>
            <select
              value={filters.location}
              onChange={(e) => setFilters({ ...filters, location: e.target.value })}
              className="luxury-input"
            >
              {venueLocations.map(location => (
                <option key={location} value={location}>{location}</option>
              ))}
            </select>
          </div>

          <div>
            <select
              value={filters.venueType}
              onChange={(e) => setFilters({ ...filters, venueType: e.target.value })}
              className="luxury-input"
            >
              {venueTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-800/50 text-white rounded-lg hover:bg-gray-700/50 transition-colors"
            >
              {viewMode === 'grid' ? <List className="w-4 h-4" /> : <Grid className="w-4 h-4" />}
              <span>{viewMode === 'grid' ? 'List' : 'Grid'}</span>
            </button>
          </div>
        </div>

        {/* Advanced Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-white/80 mb-1">Guest Count</label>
            <input
              type="number"
              value={filters.minCapacity}
              onChange={(e) => setFilters({ ...filters, minCapacity: Number(e.target.value) })}
              className="luxury-input"
              min="1"
              placeholder="Minimum capacity"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white/80 mb-1">Max Budget (LKR)</label>
            <input
              type="number"
              value={filters.maxPrice}
              onChange={(e) => setFilters({ ...filters, maxPrice: Number(e.target.value) })}
              className="luxury-input"
              min="0"
              placeholder="Maximum budget"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({
                searchTerm: '',
                maxPrice: 1000000,
                minCapacity: 50,
                venueType: 'all',
                location: 'All Locations'
              })}
              className="w-full px-4 py-2 bg-gray-800/50 text-white rounded-lg hover:bg-gray-700/50 transition-colors"
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-between">
        <p className="text-white">
          Showing {filteredVenues.length} of {venues.length} venues
        </p>
        <p className="text-gray-300">
          {savedVenues.length} venues saved
        </p>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
          <p className="text-white mt-4">Loading venues...</p>
        </div>
      )}

      {/* Venues Grid/List */}
      {!loading && (
        <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
        {filteredVenues.map((venue) => (
          <div
            key={venue.id}
            className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg overflow-hidden hover:scale-105 transition-all duration-300"
          >
            <div className="relative">
              <img
                src={venue.image}
                alt={venue.name}
                className="w-full h-48 object-cover"
              />
              <button
                onClick={() => toggleSaveVenue(venue.id)}
                className={`absolute top-4 right-4 p-2 rounded-full transition-colors ${
                  savedVenues.includes(venue.id)
                    ? 'bg-red-500 text-white'
                    : 'bg-white/80 text-gray-600 hover:bg-white'
                }`}
              >
                <Heart className={`w-5 h-5 ${savedVenues.includes(venue.id) ? 'fill-current' : ''}`} />
              </button>
              <div className="absolute top-4 left-4">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  venue.venueType === 'indoor' ? 'bg-blue-100 text-blue-800' :
                  venue.venueType === 'outdoor' ? 'bg-green-100 text-green-800' :
                  'bg-purple-100 text-purple-800'
                }`}>
                  {venue.venueType === 'hybrid' ? 'Indoor & Outdoor' : venue.venueType.charAt(0).toUpperCase() + venue.venueType.slice(1)}
                </span>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <h3 className="text-xl font-bold text-white">{venue.name}</h3>
                <div className="flex items-center space-x-2 text-white/70">
                  <MapPin className="w-4 h-4" />
                  <span>{venue.location}</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Star className="w-4 h-4 text-yellow-500 fill-current" />
                  <span className="font-medium text-white">{venue.rating}</span>
                  <span className="text-white/70">({venue.reviews} reviews)</span>
                </div>
                <div className="flex items-center space-x-2 text-white/70">
                  <Users className="w-4 h-4" />
                  <span>Up to {venue.capacity}</span>
                </div>
              </div>

              <p className="text-white/70 text-sm line-clamp-2">
                {venue.description}
              </p>

              <div className="flex flex-wrap gap-2">
                {venue.features.slice(0, 3).map((feature, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                  >
                    {feature}
                  </span>
                ))}
                {venue.features.length > 3 && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                    +{venue.features.length - 3} more
                  </span>
                )}
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-white/20">
                <div className="flex items-center space-x-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  <span className="text-xl font-bold text-white">LKR {venue.price.toLocaleString()}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <button className="flex items-center space-x-1 px-3 py-1 text-primary hover:text-primary-600 text-sm">
                    <Eye className="w-4 h-4" />
                    <span>Details</span>
                  </button>
                  <button className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors">
                    Contact
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
        </div>
      )}

      {!loading && filteredVenues.length === 0 && (
        <div className="text-center py-12 bg-gray-800/50 backdrop-blur-sm rounded-xl">
          <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No venues found</h3>
          <p className="text-gray-300 mb-6">Try adjusting your search criteria or filters</p>
          <button
            onClick={() => setFilters({
              searchTerm: '',
              maxPrice: 1000000,
              minCapacity: 50,
              venueType: 'all',
              location: 'All Locations'
            })}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600"
          >
            Reset Filters
          </button>
        </div>
      )}

      {/* AI-Powered Recommendations */}
      <div className="bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg p-6">
        <h3 className="text-xl font-bold text-white mb-4">🤖 AI Venue Recommendations</h3>
        <p className="text-white/80 mb-4">
          Based on your preferences and budget, here are some personalized suggestions:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-900/50 rounded-lg p-4">
            <p className="text-white font-medium mb-2">💡 Budget Optimization</p>
            <p className="text-white/70 text-sm">
              Consider venues with outdoor ceremony options for your {filters.minCapacity} guests. 
              Garden venues typically offer 20% better value than indoor-only options.
            </p>
          </div>
          <div className="bg-gray-900/50 rounded-lg p-4">
            <p className="text-white font-medium mb-2">📅 Seasonal Savings</p>
            <p className="text-white/70 text-sm">
              Booking during off-peak months (May-September) can save up to 30% on venue costs.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}